import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  withTextLocalizer,
  initJumioForWeb,
  selectJumioRedirectData,
  LocalizePropType,
  JumioWebDocumentType,
  selectUser,
  isKYCApp,
  setNotification,
  APP,
  initJumioForMobile,
  selectBrandSettings,
  selectDeviceLanguage,
  getNextUserOpenChallengeRoute,
  selectNetworkActivities,
  CLIENT_TYPE,
  getUser,
  isIDVerificationRequired,
  showSchufaRequiredDialog,
} from 'getaway-data-layer';
import { isDashboardScreen, navigateTo, showAlert } from '../../../../../utils';
import IDOnboardingView from './IDOnboardingView';
import { IDModal, IDSchema } from '../../../../../validation';
import { useFormik } from 'formik';
import ExternalBrowser from '../../../../components/ExternalBrowser/ExternalBrowser';
import { useQuery } from '../../../../../hooks/useQuery';

type Props = {
  localize: LocalizePropType;
};

interface Values {
  documentType: string;
}

const IDOnboardingContainer = ({ localize }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();
  const utm = query?.get('utm_source')?.toLowerCase();
  const jumioRedirectData: any = useSelector(selectJumioRedirectData);
  const user = useSelector(selectUser);
  const brandSettings = useSelector(selectBrandSettings);

  const passportAllowedCountries = brandSettings?.allowedPassportCountries
    ?.map((country) => localize(country.toUpperCase()))
    .sort((a, b) => a.localeCompare(b));

  const identityAllowedCountries = brandSettings?.allowedIDDocumentCountries
    ?.map((country) => localize(country.toUpperCase()))
    .sort((a, b) => a.localeCompare(b));

  const passportSupportedCountries =
    passportAllowedCountries?.length > 0
      ? [...passportAllowedCountries, 'other']
      : [];

  const identitySupportedCountries =
    identityAllowedCountries?.length > 0
      ? [...identityAllowedCountries, 'other']
      : [];

  const [showExternalBrowserScreen, setShowExternalBrowserScreen] =
    useState(false);
  const networkActivities: any = useSelector(selectNetworkActivities);
  const isLoading = networkActivities.some((activityType: any) =>
    [
      CLIENT_TYPE.USER_CLIENT.INIT_JUMIO_FOR_MOBILE,
      CLIENT_TYPE.USER_CLIENT.INIT_JUMIO_FOR_WEB,
      CLIENT_TYPE.USER_CLIENT.GET_USER,
    ].includes(activityType)
  );
  const language = useSelector(selectDeviceLanguage);
  const scanEnabled = user?.remainingIDSubmissionAttempts > 0;
  const showResubmissionUI =
    ['deleted', 'rejected'].includes(user?.identityDocument?.status) &&
    user?.identityDocument?.rejectedReason != null;
  const resubmissionInstruction = user?.identityDocument?.resubmitInstruction;

  const navigateToJumioScan = (data) => {
    if (!data?.redirectUrl) return;

    const jumioUrl = new URL(data.redirectUrl);

    jumioUrl.searchParams.set(
      'locale',
      language === 'deformal' ? 'de' : language
    );
    window.location.href = jumioUrl.toString();
  };

  const navigateToScan = (url) => {
    const jumioUrl = new URL(url);

    jumioUrl.searchParams.set(
      'locale',
      language === 'deformal' ? 'de' : language
    );
    window.location.href = jumioUrl.toString();
  };

  const onPressStart = (values: Values, force: boolean = false) => {
    dispatch(
      getUser((updatedUser) => {
        if (user?.blocked) {
          navigateTo(history, '/settings');
        } else if (isIDVerificationRequired(updatedUser)) {
          const isLocalhost = window?.location?.hostname === 'localhost';
          const redirectUrl = isLocalhost
            ? null
            : `${window?.location?.origin}${getNextUserOpenChallengeRoute(
                user,
                'IDOnboarding'
              )}`;

          if (scanEnabled && !isLoading) {
            if (brandSettings?.kycSettings?.jumioWebV4Enabled) {
              dispatch(
                initJumioForMobile(
                  IDFormik.values?.documentType === 'ID_CARD'
                    ? ['ID_CARD', 'PASSPORT']
                    : ['PASSPORT', 'ID_CARD'],
                  (res) => {
                    if (res?.web?.href) navigateToScan(res.web.href);
                  },
                  redirectUrl,
                  redirectUrl,
                  force,
                  (error) => {
                    if (
                      Object.keys(error.detail.data.violations)?.includes(
                        'api_error.user.redirectToWeb'
                      )
                    ) {
                      setShowExternalBrowserScreen(true);
                    } else if (
                      Object.keys(error.detail.data.violations)?.includes(
                        'api_error.user.cw_address_missing'
                      )
                    ) {
                      showSchufaRequiredDialog(
                        localize,
                        () => navigateTo(history, '/dashboard/schufa'),
                        'profile.schufa.missing'
                      );
                    } else if (!force) {
                      setShowExternalBrowserScreen(false);
                    }
                  }
                )
              );
            } else {
              dispatch(
                initJumioForWeb(
                  localize,
                  history,
                  values.documentType as JumioWebDocumentType,
                  jumioRedirectData?.transactionReference,
                  user?.id,
                  isDashboardScreen(),
                  isKYCApp(),
                  isKYCApp(),
                  (data) => {
                    navigateToJumioScan(data);
                  },
                  redirectUrl,
                  redirectUrl,
                  force
                )
              );
            }
          }
        } else {
          dispatch(
            setNotification({ message: localize('user.id.not.required') })
          );
          navigateTo(history, '/settings');
        }
      })
    );
  };

  const onClose = () => {
    if (isDashboardScreen()) {
      navigateTo(history, '/settings');
    } else {
      showAlert(
        '',
        localize('cancel.FirstSteps.dialog.message'),
        localize('cancel.FirstSteps.dialog.yes'),
        () => navigateTo(history, '/settings'),
        localize('cancel.FirstSteps.dialog.no'),
        () => {},
        true
      );
    }
  };

  const onClickDisabled = () => {
    if (!isLoading) {
      dispatch(
        setNotification({
          message: 'user.scan.attempts.out',
          type: APP.NOTIFICATION_TYPE.ERROR,
        })
      );
    }
  };

  const IDFormik = useFormik({
    initialValues: IDModal,
    validationSchema: IDSchema(localize),
    onSubmit: (v) => onPressStart(v, false),
  });

  useEffect(() => {
    dispatch(getUser());
  }, []);

  return (
    <>
      {showExternalBrowserScreen ? (
        <ExternalBrowser
          localize={localize}
          onClose={onClose}
          onSkip={() => onPressStart(IDFormik.values, true)}
        />
      ) : (
        <IDOnboardingView
          localize={localize}
          scanEnabled={scanEnabled}
          onClose={onClose}
          formik={IDFormik}
          onClickDisabled={onClickDisabled}
          isLoading={isLoading}
          showResubmissionUI={showResubmissionUI}
          resubmissionInstruction={resubmissionInstruction}
          passportSupportedCountries={passportSupportedCountries}
          identitySupportedCountries={identitySupportedCountries}
        />
      )}
    </>
  );
};

export default withTextLocalizer(IDOnboardingContainer);
